import { Injectable, EventEmitter, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { io, Socket } from 'socket.io-client';
import { Lot, MarketType } from '../components/bids-and-offers/lots.model';
import { LotDataService } from './lot-data.service';
import { UserDetails } from '../components/login/user.model';
import { UserService } from './user.service';
import { Observable, Subject } from 'rxjs';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Counter } from '../components/negotiations/negotiations.model';
import { ChatUser, ChatUsers } from '../components/chat/chat.model';
import { ChatService } from './chat.service';
import { SocketTicketService } from './socket-ticket.service';
import { environment } from 'src/environments/environment';
import { Store } from '@ngrx/store';
import { LotState } from '../store/lots-library/lots.reducer';
import { TableDataState } from '../store/table-data/table-data.reducer';
import { selectLot } from '../store/lots-library/lot.actions';
import { fetchData, removeRow } from '../store/table-data/table-data.actions';
import { selectTableSelectedMarket } from '../store/table-data/table-data.selectors';
import { fetchNegotiations } from '../store/negotiations/negotiations.actions';

function isValidMarketType(value: any): value is MarketType {
  return Object.values(MarketType).includes(value);
}

function isValidMongoId(id: any): boolean {
  return typeof id === 'string' && /^[0-9a-f]{24}$/i.test(id);
}

// Handshake tickets are single-use, so a middleware rejection ('Unauthorized')
// leaves the socket inactive — socket.io will NOT auto-reconnect. Reconnects
// are scheduled manually with exponential backoff, bounded so a persistently
// broken session can't spam the rate-limited ticket mutation (60/10min per IP).
const MAX_CONSECUTIVE_AUTH_FAILURES = 5;
const MAX_RECONNECT_DELAY_MS = 60_000;

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private socket: Socket;
  private isSocketInitialized = false; // prevent duplicate event listener registrations
  // Keep a short-term memory of processed negotiation events to avoid double handling

  selectedMarket = signal<MarketType>(null);
  userName: string = 'broker';
  toUserName: string = '*';
  userRole: number = 0;
  public newMessageReceived = new EventEmitter<string>();
  userDetails: UserDetails;

  private socketNegotiation = new Subject<any>(); // for new negotiation
  private socketCounter = new Subject<Counter>(); // for counter negotiation
  private chatList = new Subject<ChatUsers>();
  chatList$ = this.chatList.asObservable();

  // Emits when the server revoked this session (session_refresh_required) or the
  // ticket mutation came back UNAUTHENTICATED. AuthService subscribes and runs
  // refresh-then-reconnect (SocketService can't inject AuthService — cycle).
  private sessionRefreshRequired = new Subject<void>();
  sessionRefreshRequired$ = this.sessionRefreshRequired.asObservable();

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private authFailureCount = 0;
  private ticketFetchUnauthenticated = false;
  private ticketFetchThrottled = false;

  constructor(
    private lotDataService: LotDataService,
    private userService: UserService,
    private chatService: ChatService,
    private socketTicketService: SocketTicketService,
    private matSnackBar: MatSnackBar,
    private router: Router,
    private store: Store<{ activeLot: LotState; lotTableData: TableDataState }>
  ) {
    this.userService.userDetails$
      .pipe(takeUntilDestroyed())
      .subscribe((userData) => {
        // console.log('socket subs', userData);
        if (userData) {
          this.userDetails = userData;
          this.userRole = this.userDetails.role == 'broker' ? 1 : 0;
          this.userName =
            this.userDetails.first_name + ' ' + this.userDetails.last_name;
          // Initialize socket only once to avoid duplicate listeners
          if (!this.isSocketInitialized) {
            this.initializeSocket();
          }
        } else {
          // Logout (userService.clearUserData): tear down fully so a later
          // login in the same tab re-initializes the socket.
          this.teardown();
        }
      });

    this.store
      .select(selectTableSelectedMarket)
      .pipe(takeUntilDestroyed())
      .subscribe((market) => this.selectedMarket.set(market));
  }

  // Mints a fresh single-use ticket for the next handshake attempt. Resolves
  // null on failure and records why, so connect_error can pick the right recovery.
  private fetchSocketTicket(): Promise<string | null> {
    return new Promise((resolve) => {
      this.socketTicketService.mutate().subscribe({
        next: (res) => resolve(res.data?.createSocketTicket?.ticket ?? null),
        error: (err) => {
          const codes: string[] = (err?.graphQLErrors ?? []).map(
            (e: any) => e?.extensions?.code
          );
          if (codes.includes('UNAUTHENTICATED')) {
            this.ticketFetchUnauthenticated = true;
          }
          if (codes.includes('TOO_MANY_REQUESTS')) {
            this.ticketFetchThrottled = true;
          }
          console.error('Failed to fetch socket ticket:', err?.message ?? err);
          resolve(null);
        },
      });
    });
  }

  initializeSocket() {
    this.isSocketInitialized = true;

    // If a previous socket exists, make sure to remove its listeners and disconnect
    if (this.socket) {
      try {
        this.socket.removeAllListeners();
        if (this.socket.connected) {
          this.socket.disconnect();
        }
      } catch {}
    }

    this.socket = io(environment.socketServer, {
      autoConnect: false,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      // Runs on every connection attempt (including socket.io's automatic
      // reconnects after transport errors), so each attempt gets a fresh ticket.
      auth: (cb) => {
        this.fetchSocketTicket().then((ticket) => cb({ ticket: ticket ?? '' }));
      },
    });

    this.socket.on('connect', () => {
      // Identity and rooms come from the consumed ticket server-side; there is
      // no 'join' handler on the backend anymore.
      this.authFailureCount = 0;
      this.ticketFetchThrottled = false;
    });

    this.socket.on('connect_error', (err) => {
      if (err.message === 'Unauthorized') {
        this.authFailureCount++;
        if (this.ticketFetchUnauthenticated) {
          this.ticketFetchUnauthenticated = false;
          if (this.authFailureCount <= MAX_CONSECUTIVE_AUTH_FAILURES) {
            this.sessionRefreshRequired.next();
          } else {
            console.error(
              'Socket auth failed repeatedly — real-time features disabled'
            );
          }
          return;
        }
        if (this.authFailureCount > MAX_CONSECUTIVE_AUTH_FAILURES) {
          console.error(
            'Socket auth failed repeatedly — real-time features disabled'
          );
          return;
        }
        this.scheduleReconnect();
      } else {
        // Transport-level errors: socket.io auto-reconnects on its own.
        console.error('Socket connection error:', err.message);
      }
    });

    this.socket.on('session_refresh_required', () => {
      // Server bumps auth_version and force-disconnects right after emitting
      // this — the current access token is stale. AuthService refreshes and
      // calls reconnect() (or logs out if the refresh token is dead too).
      this.sessionRefreshRequired.next();
    });

    this.socket.on('joined', (data: string) => {
      // console.log(`Joined. ${data}`, data);
      this.getUsers();
    });

    this.socket.on('left', (data) => {
      this.getUsers();
    });

    this.socket.on('message', (data: string) => {
      // console.log('Message received:', data);
      // const obj = JSON.parse(data ) || {}
      // this.newMessageReceived.emit(obj['text']);
      this.newMessageReceived.emit(data['msg']);
    });

    this.socket.on('left', (reason) => {
      // console.log(
      //   `Smb left the chat. Socket.id = ${this.socket.id}. Reason: ${reason}`
      // );
    });

    this.socket.on('disconnect', (reason) => {
      // console.log(
      //   `Disconnected. Socket.id = ${this.socket.id}. Reason: ${reason}`
      // );
    });

    this.socket.on('users', (data: any) => {
      // let users: ChatUsers;
      //   users['users'] = [dt];
      const allEntry: ChatUser = { user_id: null, name: '*', socket: null };
      data.unshift({ user_id: null, name: '*', socket: null } as ChatUser);
      this.chatList.next(data);
    });

    this.socket.on('newLot', (data: Lot) => {
      if (!isValidMarketType(data.market_type)) {
        console.error(
          `Received newLot with invalid market_type: ${data.market_type}`
        );
        return;
      }
      this.store.dispatch(
        fetchData({ limit: 40, marketType: MarketType[data.market_type] })
      );
      this.lotDataService.addLot(data);
      const snackBarRef = this.openSnackBar(
        `New ${data['lot_type']} created: ${data['commodity']} - ${data['grade']}`,
        'see details'
      );
      snackBarRef.onAction().subscribe(() => {
        if (
          data['user_id'] !== this.userDetails._id &&
          isValidMongoId(data['_id'])
        ) {
          this.router.navigate(['app/lots', data['_id']]);
        }
      });
    });

    this.socket.on('lotCancelled', (data: any) => {
      // const snackBarRef = this.openSnackBar(`Lot ${data['commodity']}  - ${data['grade']} cancelled`, 'close');
      const snackBarRef = this.openSnackBar(`Lot cancelled`, 'close');
      this.store.dispatch(removeRow({ lotId: data._id }));
      // this.store.dispatch(fetchData())
      // snackBarRef = ...something
    });

    this.socket.on('newNegotiation', (data: any) => {
      if (!isValidMongoId(data.lot_id)) {
        console.error(
          `Received newNegotiation with invalid lot_id: ${data.lot_id}`
        );
        return;
      }
      this.store.dispatch(
        selectLot({ lotDetails: { _id: data.lot_id } as Lot })
      );
      this.store.dispatch(fetchNegotiations());
      // this.socketNegotiation.next(data);
      // const snackBarRef = this.matSnackBar.openFromComponent(CustomSnackBarComponent,{
      //   data: `New Negotiation created for ${data['commodity']} - ${data['grade']}`,
      //   duration: 35000,
      // });

      // TODO: first check if data.user_id is same as current user id. If it is, don't show the snackbar
      const snackBarRef = this.openSnackBar(
        `New Negotiation created for ${data['commodity']} - ${data['grade']}`,
        'See Details'
      );

      snackBarRef.onAction().subscribe(() => {
        // clicked 'see details', go to negotiations path with specific id
        if (isValidMongoId(data['_id'])) {
          this.router.navigate(['app/negotiations', data['_id']]);
        }
      });
    });

    this.socket.on('negotiationDeclined', (data: any) => {
      this.openSnackBar(`Negotiation Declined`, 'close');
      this.store.dispatch(fetchNegotiations());
    });

    this.socket.on('newCounter', (data: any) => {
      this.socketCounter.next(data);
      if (data['counters']?.[0]?.['originator_id'] !== this.userDetails._id) {
        if (isValidMongoId(data['lot_id'])) {
          this.store.dispatch(
            selectLot({ lotDetails: { ...data, _id: data['lot_id'] } as Lot })
          );
        }

        const snackBarTitle = [
          data['commodity'],
          data['product_type'],
          data['grade'],
        ]
          .filter(Boolean)
          .join('-');
        const snackBarRef = this.openSnackBar(
          `New Counter created for ${snackBarTitle}`,
          'See Details'
        );
        snackBarRef.onAction().subscribe(() => {
          if (isValidMongoId(data['_id'])) {
            this.router.navigate(['app/negotiations', data['_id']]);
          }
        });
      }
    });

    this.socket.on('newContract', (data: any) => {
      const snackBarRef = this.openSnackBar(
        `New Contract created for ${data['commodity']} - ${data['grade']}`,
        'See Details'
      );

      snackBarRef.onAction().subscribe(() => {
        // clicked 'see details', go to negotiations path with specific id
        if (isValidMongoId(data['_id'])) {
          this.router.navigate(['app/contracts', data['_id']]);
        }
      });
      this.store.dispatch(fetchNegotiations());
    });

    // Listeners must all be registered before the first handshake (autoConnect
    // is off so no event can arrive early).
    this.socket.connect();
  }

  // Manual retry after an 'Unauthorized' handshake rejection (socket.io won't
  // auto-reconnect from those). Exponential backoff; a throttled ticket
  // mutation forces at least a full minute so the 60/10min IP limit can recover.
  private scheduleReconnect() {
    this.clearReconnectTimer();
    let delay = Math.min(1000 * 2 ** this.authFailureCount, MAX_RECONNECT_DELAY_MS);
    if (this.ticketFetchThrottled) {
      this.ticketFetchThrottled = false;
      delay = MAX_RECONNECT_DELAY_MS;
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.socket.connect();
    }, delay);
  }

  // Called by AuthService after a successful token refresh. Deliberately does
  // not reset authFailureCount — only a successful connect does — so a
  // refresh/reject loop stays bounded by MAX_CONSECUTIVE_AUTH_FAILURES.
  reconnect() {
    if (this.socket && !this.socket.connected) {
      this.clearReconnectTimer();
      this.socket.connect();
    }
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private teardown() {
    this.clearReconnectTimer();
    this.authFailureCount = 0;
    this.ticketFetchUnauthenticated = false;
    this.ticketFetchThrottled = false;
    if (this.socket) {
      try {
        this.socket.removeAllListeners();
        this.socket.disconnect();
      } catch {}
    }
    this.isSocketInitialized = false;
    this.userDetails = null;
  }

  public sendMessage(message: string) {
    const obj = {
      name: this.userName,
      role: this.userRole,
      to: this.toUserName,
      msg: message,
    };
    this.socket.emit('messageTo', obj);
    // console.log('Message sent:', obj);
  }

  getUsers() {
    this.socket.emit('getUsers', { name: this.userName, role: this.userRole });
  }

  public disconnect() {
    // Also cancel any pending auth-retry so logout can't leave a loop
    // requesting tickets with a dead session.
    this.clearReconnectTimer();
    if (this.socket?.connected) {
      this.socket.disconnect();
    }
  }

  getMessages() {
    // const data = this.chatService.getUserMessages():
    // const obj = JSON.parse(data ) || {}
    // this.newMessageReceived.emit(obj['text']);
  }

  getSocketNegotiationObservable(): Observable<any> {
    return this.socketNegotiation.asObservable();
  }

  getSocketCounterObservable(): Observable<Counter> {
    return this.socketCounter.asObservable();
  }

  openSnackBar(
    msg: string,
    action: string,
    config: object = { duration: 8000, panelClass: ['blue-snackbar'] }
  ): MatSnackBarRef<any> {
    return this.matSnackBar.open(msg, action, config);
  }

  // Other methods...
}
